/**
 * Unified Analytics Page
 * 
 * Single entry point for user account analytics:
 * - Overview (default) - High-level summary combining key metrics
 * - Agent & Usage Analytics - Calls, performance, costs
 * - Voice Analytics (if enabled)
 * 
 * Note: This is for user's account analytics, NOT platform/posthog analytics.
 * 
 * Role-based views:
 * - Admin/Primary Admin: Full analytics (assessments, opportunities, agents, etc.)
 * - Member/Employee: Only their assigned agents and sessions
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { usePlan } from '../context/PlanContext';
import { useOrganization } from '../context/OrganizationContext';
import { useUserRole } from '../hooks/useUserRole';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';

import {
  ArrowRight,
  ArrowUpRight,
  ChartBar,
  ChatText,
  CheckCircle,
  Clock,
  CurrencyDollar,
  FileText,
  Lightning,
  Lock,
  Microphone,
  Phone,
  Pulse,
  Robot,
  Target,
  TrendUp,
  UsersThree
} from '@phosphor-icons/react';
// Tab configuration for ADMIN users
const ADMIN_TABS = [
  { id: 'overview', label: 'Overview', icon: ChartBar },
  { id: 'agents', label: 'Agent & Usage', icon: Robot, requiresPro: true },
  { id: 'voice', label: 'Voice Analytics', icon: Microphone, requiresPartnerPro: true },
];

// Tab configuration for MEMBER users (simplified)
const MEMBER_TABS = [
  { id: 'my-agents', label: 'My Agents', icon: Robot },
];

// Funnel step component for conversion funnel
const FunnelStep = ({ label, value, percentage }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-3 flex-1">
      <div className="w-2 h-2 rounded-full bg-[#6366F1]" />
      <span className="text-sm text-[#A0AEC0]">{label}</span>
    </div>
    <div className="flex items-center gap-3">
      <span className="text-sm font-semibold text-white">{value}</span>
      <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full bg-[#6366F1]" style={{ width: `${Math.min(percentage, 100)}%` }} />
      </div>
      <span className="text-xs text-[#718096] w-8 text-right">{percentage}%</span>
    </div>
  </div>
);

// Member Overview Tab - Shows only their assigned agents
const MemberOverviewTab = ({ orgId, loading: parentLoading }) => {
  const [agents, setAgents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMemberData = async () => {
      if (!orgId) return;
      try {
        setLoading(true);
        // Fetch agents assigned to this member
        const agentsRes = await fetchWithOrg(
          `${BACKEND_URL}/api/my/agents`,
          {},
          orgId
        );
        if (agentsRes.ok) {
          const agentsData = await agentsRes.json();
          setAgents(agentsData.agents || []);
        }

        // Fetch recent sessions for this member
        const sessionsRes = await fetchWithOrg(
          `${BACKEND_URL}/api/analytics/my-sessions?limit=10`,
          {},
          orgId
        );
        if (sessionsRes.ok) {
          const sessionsData = await sessionsRes.json();
          setSessions(sessionsData.sessions || []);
        }
      } catch (err) {
        console.error('Error fetching member data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMemberData();
  }, [orgId]);

  if (loading || parentLoading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-6">
      {/* Summary KPIs for Member */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard
          icon={Robot}
          label="Assigned Agents"
          value={agents.length}
          subtext="Available to you"
          color="from-[#6366F1] to-[#8B5CF6]"
          loading={false}
        />
        <KPICard
          icon={Pulse}
          label="Recent Sessions"
          value={sessions.length}
          subtext="Last 30 days"
          color="from-[#14F195] to-[#10B981]"
          loading={false}
        />
        <KPICard
          icon={Clock}
          label="Total Usage"
          value={sessions.reduce((acc, s) => acc + (s.duration_minutes || 0), 0).toFixed(1)}
          subtext="Minutes"
          color="from-[#F59E0B] to-[#FBBF24]"
          loading={false}
        />
      </div>

      {/* My Assigned Agents */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Robot size={20} className="text-[#6366F1]" />
          My Assigned Agents
        </h3>
        {agents.length > 0 ? (
          <div className="space-y-3">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${agent.status === 'active' ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
                    <Robot size={20} className={agent.status === 'active' ? 'text-green-400' : 'text-gray-400'} />
                  </div>
                  <div>
                    <p className="text-white font-medium">{agent.name}</p>
                    <p className="text-gray-400 text-sm">{agent.category || agent.department || 'AI Agent'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`px-2 py-1 rounded text-xs ${
                    agent.status === 'active' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {agent.status || 'inactive'}
                  </span>
                  {agent.is_voice_template && (
                    <Phone size={16} className="text-[#6366F1]" />
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <Robot size={48} className="mx-auto mb-4 opacity-50" />
            <p>No agents have been assigned to you yet</p>
            <p className="text-sm mt-2">Contact your administrator to get access to AI agents</p>
          </div>
        )}
      </div>

      {/* Recent Sessions */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Pulse size={20} className="text-[#14F195]" />
          Recent Pulse
        </h3>
        {sessions.length > 0 ? (
          <div className="space-y-3">
            {sessions.map((session, idx) => (
              <div
                key={session.id || idx}
                className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/10 rounded-lg">
                    <ChatText size={16} className="text-gray-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm">{session.agent_name || 'Agent Session'}</p>
                    <p className="text-gray-500 text-xs">
                      {session.created_at ? new Date(session.created_at).toLocaleDateString() : 'Recently'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white text-sm">{session.duration_minutes?.toFixed(1) || '0'} min</p>
                  <p className="text-gray-500 text-xs">{session.messages_count || 0} messages</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <Pulse size={48} className="mx-auto mb-4 opacity-50" />
            <p>No recent activity</p>
            <p className="text-sm mt-2">Start using your assigned agents to see activity here</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Overview Tab Component (ADMIN ONLY)
const OverviewTab = ({ data, loading, userPlan }) => {
  const isBasic = !userPlan || userPlan === 'basic' || userPlan === 'starter';
  
  return (
    <div className="space-y-6">
      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={FileText}
          label="Assessments"
          value={data?.assessments_completed || 0}
          subtext={`${data?.assessments_started || 0} started`}
          color="from-[#6366F1] to-[#8B5CF6]"
          loading={loading}
        />
        <KPICard
          icon={Lightning}
          label="Opportunities"
          value={data?.opportunities_generated || 0}
          subtext="Generated"
          color="from-[#14F195] to-[#10B981]"
          loading={loading}
        />
        <KPICard
          icon={Robot}
          label="Agents"
          value={data?.agents_created || 0}
          subtext={`${data?.agents_executed || 0} executed`}
          color="from-[#EC4899] to-[#F472B6]"
          loading={loading}
        />
        <KPICard
          icon={UsersThree}
          label="Plan"
          value={userPlan?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Basic'}
          subtext="Current tier"
          color="from-[#F59E0B] to-[#FBBF24]"
          loading={loading}
        />
      </div>

      {/* Quick Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Filter Preview */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Target size={20} className="text-[#6366F1]" />
            Conversion Filter
          </h3>
          <div className="space-y-3">
            <FunnelStep label="Assessments Started" value={data?.assessments_started || 0} percentage={data?.assessments_started > 0 ? 100 : 0} />
            <FunnelStep label="Assessments Completed" value={data?.assessments_completed || 0} percentage={data?.assessments_started ? Math.round((data.assessments_completed / data.assessments_started) * 100) : 0} />
            <FunnelStep label="Opportunities Generated" value={data?.opportunities_generated || 0} percentage={data?.assessments_completed ? Math.round((data.opportunities_generated / data.assessments_completed) * 100) : 0} />
            <FunnelStep label="Agents Created" value={data?.agents_created || 0} percentage={data?.opportunities_generated ? Math.round((data.agents_created / data.opportunities_generated) * 100) : 0} />
          </div>
        </div>

        {/* Recent Pulse */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Pulse size={20} className="text-[#14F195]" />
            Quick Actions
          </h3>
          <div className="space-y-3">
            <QuickActionCard
              icon={FileText}
              title="Run New Assessment"
              description="Discover AI opportunities in your business"
              href="/assessments"
            />
            {isBasic ? (
              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg border border-white/10 opacity-60">
                <div className="p-2 bg-[#6366F1]/20 rounded-lg">
                  <Robot size={20} className="text-[#6366F1]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="text-white font-medium">View All Agents</h4>
                    <Lock size={14} className="text-gray-500" />
                  </div>
                  <p className="text-[#718096] text-sm">Upgrade to Pro to access AI agents</p>
                </div>
              </div>
            ) : (
              <QuickActionCard
                icon={Robot}
                title="View All Agents"
                description="Manage your AI workforce"
                href="/partner-pro/ai-workforce/agents"
              />
            )}
            <QuickActionCard
              icon={Lightning}
              title="Explore Marketplace"
              description="Find pre-built AI agents"
              href="/partner-pro/ai-workforce/marketplace-v2"
            />
          </div>
        </div>
      </div>

      {/* Upgrade CTA for Basic UsersThree */}
      {isBasic && (
        <div className="bg-gradient-to-r from-[#6366F1]/10 to-[#EC4899]/10 border border-[#6366F1]/30 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#6366F1]/20 rounded-xl">
                <TrendUp size={24} className="text-[#6366F1]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Unlock Advanced Analytics</h3>
                <p className="text-[#A0AEC0] text-sm">Get deeper insights with Pro: detailed funnels, cohort analysis, and usage trends</p>
              </div>
            </div>
            <a
              href="/pricing"
              className="px-6 py-2.5 bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
            >
              Upgrade to Pro
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// Agent & Usage Analytics Tab
const AgentUsageTab = ({ orgId, userPlan }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);
  const isBasic = !userPlan || userPlan === 'basic' || userPlan === 'starter';

  useEffect(() => {
    if (isBasic) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      if (!orgId) return;
      try {
        setLoading(true);
        const res = await fetchWithOrg(
          `${BACKEND_URL}/api/analytics/agent-usage?days=${dateRange}`,
          {},
          orgId
        );
        if (res.ok) {
          const json = await res.json();
          setData(json);
        }
      } catch (err) {
        console.error('Error fetching agent analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId, dateRange, isBasic]);

  // Show locked state for Basic users
  if (isBasic) {
    return (
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-[#6366F1]/20 rounded-full flex items-center justify-center">
          <Lock size={32} className="text-[#6366F1]" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Agent & Usage Analytics</h3>
        <p className="text-gray-400 mb-6 max-w-md mx-auto">
          Detailed agent analytics including run history, performance metrics, and usage trends requires a Pro or Partner Pro plan.
        </p>
        <a
          href="/pricing"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
        >
          Upgrade to Pro
          <ArrowRight size={16} />
        </a>
      </div>
    );
  }

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="flex justify-end">
        <select
          value={dateRange}
          onChange={(e) => setDateRange(Number(e.target.value))}
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Usage KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          icon={Pulse}
          label="Total Calls"
          value={data?.total_calls || 0}
          color="from-[#6366F1] to-[#8B5CF6]"
          loading={loading}
        />
        <KPICard
          icon={CheckCircle}
          label="Success Rate"
          value={`${data?.success_rate || 0}%`}
          color="from-[#14F195] to-[#10B981]"
          loading={loading}
        />
        <KPICard
          icon={Clock}
          label="Avg Response"
          value={`${data?.avg_response_time || 0}ms`}
          color="from-[#F59E0B] to-[#FBBF24]"
          loading={loading}
        />
        <KPICard
          icon={CurrencyDollar}
          label="Est. Cost"
          value={`$${(data?.total_cost || 0).toFixed(2)}`}
          color="from-[#EC4899] to-[#F472B6]"
          loading={loading}
        />
      </div>

      {/* Usage Trends Chart */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Usage Trends</h3>
        {data?.trends?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.trends}>
              <defs>
                <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="date" stroke="#666" tick={{ fill: '#999' }} />
              <YAxis stroke="#666" tick={{ fill: '#999' }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Area type="monotone" dataKey="calls" stroke="#6366F1" fillOpacity={1} fill="url(#colorCalls)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400">
            No usage data available for this period
          </div>
        )}
      </div>

      {/* Agent Performance Table */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Agent Performance</h3>
        {data?.agents?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-sm text-gray-400">Agent</th>
                  <th className="text-right py-3 px-4 text-sm text-gray-400">Calls</th>
                  <th className="text-right py-3 px-4 text-sm text-gray-400">Success Rate</th>
                  <th className="text-right py-3 px-4 text-sm text-gray-400">Avg Response</th>
                </tr>
              </thead>
              <tbody>
                {data.agents.map((agent, idx) => (
                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-3 px-4 text-white">{agent.name}</td>
                    <td className="py-3 px-4 text-right text-gray-300">{agent.calls}</td>
                    <td className="py-3 px-4 text-right text-green-400">{agent.success_rate}%</td>
                    <td className="py-3 px-4 text-right text-gray-300">{agent.avg_response}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No agent performance data yet</p>
        )}
      </div>
    </div>
  );
};

// Voice Analytics Tab (Pro and Partner Pro)
const VoiceAnalyticsTab = ({ userPlan, orgId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(30);
  
  const isPro = userPlan === 'pro';
  const isPartnerPro = userPlan === 'partner_pro' || userPlan === 'partner-pro';
  const hasVoiceAccess = isPro || isPartnerPro;

  useEffect(() => {
    const fetchVoiceAnalytics = async () => {
      if (!hasVoiceAccess) {
        setLoading(false);
        return;
      }
      if (!orgId) {
        // No org selected, show empty state
        setLoading(false);
        setData(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await fetchWithOrg(
          `${BACKEND_URL}/api/analytics/voice/summary?days=${timeRange}`,
          {},
          orgId
        );
        if (res.ok) {
          const result = await res.json();
          if (result.success && result.data) {
            setData(result.data);
          } else {
            setData(null);
          }
        } else {
          const errorData = await res.json().catch(() => ({}));
          let errorMessage = 'Failed to load voice analytics';
          if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map(e => typeof e === 'string' ? e : e.msg || e.type || JSON.stringify(e)).join(', ');
            }
          }
          setError(errorMessage);
        }
      } catch (err) {
        console.error('Error fetching voice analytics:', err);
        setError('Error loading voice analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchVoiceAnalytics();
  }, [orgId, timeRange, hasVoiceAccess]);

  if (!hasVoiceAccess) {
    return (
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-[#6366F1]/20 rounded-full flex items-center justify-center">
          <Lock size={32} className="text-[#6366F1]" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Voice Analytics</h3>
        <p className="text-gray-400 mb-6 max-w-md mx-auto">
          Voice analytics including call recordings, transcripts, and sentiment analysis requires a Pro or Partner Pro plan.
        </p>
        <a
          href="/pricing"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
        >
          Upgrade to Pro
          <ArrowRight size={16} />
        </a>
      </div>
    );
  }

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <div className="bg-white/5 backdrop-blur-sm border border-red-500/30 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <Pulse size={24} className="text-red-400" />
          <div>
            <h3 className="text-white font-semibold">Error Loading Analytics</h3>
            <p className="text-gray-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data) {
    return (
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center">
        <Microphone size={64} className="mx-auto mb-6 text-[#6366F1] opacity-50" />
        <h3 className="text-xl font-semibold text-white mb-2">No Voice Data Yet</h3>
        <p className="text-gray-400">
          Start using voice agents to see analytics here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-end gap-2">
        {[7, 30, 90].map((days) => (
          <button
            key={days}
            onClick={() => setTimeRange(days)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              timeRange === days
                ? 'bg-[#6366F1] text-white'
                : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
            }`}
          >
            {days} Days
          </button>
        ))}
      </div>

      {/* Voice KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          icon={Clock}
          label="Voice Minutes"
          value={data.total_minutes?.toFixed(1) || '0'}
          subtext={`Last ${timeRange} days`}
          color="from-[#6366F1] to-[#8B5CF6]"
          loading={false}
        />
        <KPICard
          icon={Pulse}
          label="Interactions"
          value={data.total_interactions || 0}
          subtext="Voice conversations"
          color="from-[#14F195] to-[#10B981]"
          loading={false}
        />
        <KPICard
          icon={Microphone}
          label="STT Calls"
          value={data.total_stt_calls || 0}
          subtext="Speech-to-text"
          color="from-[#F59E0B] to-[#FBBF24]"
          loading={false}
        />
        <KPICard
          icon={TrendUp}
          label="TTS Calls"
          value={data.total_tts_calls || 0}
          subtext="Text-to-speech"
          color="from-[#EC4899] to-[#F472B6]"
          loading={false}
        />
      </div>

      {/* Daily Voice Usage Chart */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Voice Minutes Per Day</h3>
        {data.daily_minutes && data.daily_minutes.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.daily_minutes}>
              <defs>
                <linearGradient id="colorVoice" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="date" stroke="#666" tick={{ fill: '#999' }} />
              <YAxis stroke="#666" tick={{ fill: '#999' }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Area type="monotone" dataKey="minutes" stroke="#6366F1" fillOpacity={1} fill="url(#colorVoice)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400">
            No voice usage data available for this period
          </div>
        )}
      </div>

      {/* Top Voice Agents */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Top Voice Agents</h3>
        {data.top_agents && data.top_agents.length > 0 ? (
          <div className="space-y-3">
            {data.top_agents.map((agent, index) => (
              <div
                key={agent.agent_id || index}
                className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center font-bold text-white">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{agent.agent_name || 'Unnamed Agent'}</h4>
                    <p className="text-gray-400 text-sm">{agent.interactions || 0} interactions</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold">{agent.minutes?.toFixed(1) || 0} min</p>
                  <p className="text-gray-400 text-sm">voice time</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <Robot size={48} className="mx-auto mb-4 opacity-50" />
            <p>No voice-enabled agents have been used yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Shared Components
const KPICard = ({ icon: Icon, label, value, subtext, color, loading }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5"
  >
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2 bg-gradient-to-br ${color} rounded-lg`}>
        <Icon size={18} className="text-white" />
      </div>
      <span className="text-sm text-gray-400">{label}</span>
    </div>
    {loading ? (
      <div className="h-8 w-20 bg-white/10 rounded animate-pulse"></div>
    ) : (
      <>
        <p className="text-2xl font-bold text-white">{value}</p>
        {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
      </>
    )}
  </motion.div>
);

const FilterStep = ({ label, value, percentage }) => (
  <div className="flex items-center gap-4">
    <div className="flex-1">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-400">{label}</span>
        <span className="text-sm text-white font-medium">{value}</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-full transition-all"
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
    <span className="text-sm text-gray-500 w-12 text-right">{percentage}%</span>
  </div>
);

const QuickActionCard = ({ icon: Icon, title, description, href }) => (
  <a
    href={href}
    className="flex items-center gap-4 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors group"
  >
    <div className="p-2 bg-white/10 rounded-lg group-hover:bg-[#6366F1]/20 transition-colors">
      <Icon size={18} className="text-gray-400 group-hover:text-[#6366F1] transition-colors" />
    </div>
    <div className="flex-1">
      <p className="text-white font-medium text-sm">{title}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
    <ArrowUpRight size={16} className="text-gray-500 group-hover:text-[#6366F1] transition-colors" />
  </a>
);

const LoadingState = () => (
  <div className="space-y-4">
    {[1, 2, 3].map(i => (
      <div key={i} className="h-32 bg-white/5 rounded-xl animate-pulse" />
    ))}
  </div>
);

// Main Component
const UnifiedAnalytics = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userPlan } = usePlan();
  const { activeOrganization } = useOrganization();
  const { role, isAdmin, loading: roleLoading } = useUserRole();
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isPro = userPlan === 'pro';
  const isPartnerPro = userPlan === 'partner_pro' || userPlan === 'partner-pro';
  const hasVoiceAccess = isPro || isPartnerPro;

  // Determine if user is a member (not admin)
  const isMemberRole = role === 'member' || role === 'employee' || role === 'viewer';
  
  // Set default tab based on role
  const defaultTab = isMemberRole ? 'my-agents' : 'overview';
  const activeTab = searchParams.get('tab') || defaultTab;

  // Fetch overview data (only for admins)
  useEffect(() => {
    if (isMemberRole) {
      setLoading(false);
      return;
    }
    
    const fetchOverview = async () => {
      if (!activeOrganization?.organization_id) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetchWithOrg(
          `${BACKEND_URL}/api/analytics/insights/summary?period=30d`,
          {},
          activeOrganization.organization_id
        );
        if (res.ok) {
          const json = await res.json();
          setOverviewData(json.kpis?.reduce((acc, kpi) => ({ ...acc, [kpi.id]: kpi.value }), {}) || {});
        } else {
          setOverviewData({});
        }
      } catch (err) {
        console.error('Error fetching overview:', err);
        setOverviewData({});
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, [activeOrganization, isMemberRole]);

  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  const isBasic = !userPlan || userPlan === 'basic' || userPlan === 'starter';
  
  // Get tabs based on role
  const visibleTabs = isMemberRole 
    ? MEMBER_TABS 
    : ADMIN_TABS.filter(tab => {
        if (tab.requiresPartnerPro) return hasVoiceAccess;
        return true;
      });

  // Show loading state while role is being fetched
  if (roleLoading) {
    return (
      <div className="min-h-screen bg-[#161B26] p-6">
        <div className="max-w-7xl mx-auto">
          <LoadingState />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161B26] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {isMemberRole ? 'My Workspace' : 'Analytics'}
          </h1>
          <p className="text-gray-400">
            {isMemberRole 
              ? 'View your assigned agents and activity' 
              : 'Monitor your AI operations and business metrics'
            }
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10 pb-2 overflow-x-auto">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const isLocked = !isMemberRole && isBasic && tab.requiresPro;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-[#6366F1] text-white'
                    : isLocked 
                      ? 'text-gray-500 hover:text-gray-400'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon size={16} />
                {tab.label}
                {isLocked && <Lock size={12} className="ml-1" />}
              </button>
            );
          })}
          
          {/* Show locked Voice tab for admin users without voice access */}
          {!isMemberRole && !hasVoiceAccess && (
            <button
              onClick={() => setActiveTab('voice')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-400 transition-all whitespace-nowrap"
            >
              <Microphone size={16} />
              Voice Analytics
              <Lock size={12} className="ml-1" />
            </button>
          )}
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Member Views */}
          {isMemberRole && activeTab === 'my-agents' && (
            <MemberOverviewTab orgId={activeOrganization?.organization_id} loading={loading} />
          )}
          
          {/* Admin Views */}
          {!isMemberRole && activeTab === 'overview' && (
            <OverviewTab data={overviewData} loading={loading} userPlan={userPlan} />
          )}
          {!isMemberRole && activeTab === 'agents' && (
            <AgentUsageTab orgId={activeOrganization?.organization_id} userPlan={userPlan} />
          )}
          {!isMemberRole && activeTab === 'voice' && (
            <VoiceAnalyticsTab userPlan={userPlan} orgId={activeOrganization?.organization_id} />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default UnifiedAnalytics;
